<template>
  <!--
    78-card live progress grid for the Custom Deck builder's
    committed phase. Canonical card order, placeholder slots fill in
    as gpt-image-2 returns each card. Polling cycle in CustomDeckBuilder
    re-fetches the preview every 5s.

    The next ``inFlightCount`` empty slots in canonical order render
    with a pulsing border — visual cue for which 8 cards the
    ThreadPoolExecutor is currently working on. Once the deck is
    ``done`` (creation_phase='approved'), all empty slots stay quiet.
  -->
  <div class="full-deck-grid">
    <header class="grid-head">
      <div class="grid-sub">
        {{ filledCount }} / {{ CARD_ORDER.length }} cards
      </div>
    </header>

    <div class="grid">
      <div
        v-for="(slot, idx) in slots"
        :key="slot.name + idx"
        class="slot"
        :class="{
          filled: !!slot.imageUrl,
          inflight: slot.inFlight,
          clickable: !!slot.imageUrl,
          'slot-back': slot.isBack,
        }"
        @click="slot.imageUrl && $emit('open', slot)"
      >
        <div class="slot-frame">
          <img
            v-if="slot.imageUrl"
            :src="slot.imageUrl"
            :alt="slot.name"
            class="slot-img"
            loading="lazy"
          />
          <div v-else class="slot-placeholder">
            <span v-if="slot.inFlight" class="spinner-dot"></span>
            <span v-else class="dash"> - </span>
          </div>
        </div>
        <div class="slot-name">{{ slot.name }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // Array of {card_id, card_name, image_url} from the preview endpoint.
  cards: { type: Array, default: () => [] },
  deckName: { type: String, default: '' },
  done: { type: Boolean, default: false },
  // Card-back image URL for the deck. When set, prepended as the
  // first slot (labeled "Card Back") so the operator can preview /
  // regenerate it inline alongside the 78 cards.
  backUrl: { type: String, default: '' },
  // How many of the next-empty slots to mark as "in flight" — set to
  // DECK_GEN_PARALLEL_WORKERS (default 8) so the visual matches what
  // the BE thread pool is actually crunching.
  inFlightCount: { type: Number, default: 8 },
})
defineEmits(['open'])

// Canonical 78-card order. Major Arcana (0-21) followed by the four
// minor suits in Ace → King order. Names match TarotCard.name in the
// BE seed exactly so the grid lookup hits.
const CARD_ORDER = [
  'The Fool', 'The Magician', 'The High Priestess', 'The Empress', 'The Emperor',
  'The Hierophant', 'The Lovers', 'The Chariot', 'Strength', 'The Hermit',
  'Wheel of Fortune', 'Justice', 'The Hanged Man', 'Death', 'Temperance',
  'The Devil', 'The Tower', 'The Star', 'The Moon', 'The Sun',
  'Judgement', 'The World',
  // Cups
  'Ace of Cups', 'Two of Cups', 'Three of Cups', 'Four of Cups', 'Five of Cups',
  'Six of Cups', 'Seven of Cups', 'Eight of Cups', 'Nine of Cups', 'Ten of Cups',
  'Page of Cups', 'Knight of Cups', 'Queen of Cups', 'King of Cups',
  // Wands
  'Ace of Wands', 'Two of Wands', 'Three of Wands', 'Four of Wands', 'Five of Wands',
  'Six of Wands', 'Seven of Wands', 'Eight of Wands', 'Nine of Wands', 'Ten of Wands',
  'Page of Wands', 'Knight of Wands', 'Queen of Wands', 'King of Wands',
  // Swords
  'Ace of Swords', 'Two of Swords', 'Three of Swords', 'Four of Swords', 'Five of Swords',
  'Six of Swords', 'Seven of Swords', 'Eight of Swords', 'Nine of Swords', 'Ten of Swords',
  'Page of Swords', 'Knight of Swords', 'Queen of Swords', 'King of Swords',
  // Pentacles
  'Ace of Pentacles', 'Two of Pentacles', 'Three of Pentacles', 'Four of Pentacles', 'Five of Pentacles',
  'Six of Pentacles', 'Seven of Pentacles', 'Eight of Pentacles', 'Nine of Pentacles', 'Ten of Pentacles',
  'Page of Pentacles', 'Knight of Pentacles', 'Queen of Pentacles', 'King of Pentacles',
]

const slots = computed(() => {
  // First pass: build slots with image URLs.
  const built = CARD_ORDER.map((name) => {
    const match = props.cards.find(c => (c.card_name || c.name) === name)
    return {
      name,
      cardId: match?.card_id || null,
      imageUrl: match?.image_url || '',
      inFlight: false,
      isBack: false,
    }
  })
  // Second pass: mark the next N empty slots as in-flight while gen
  // is still running. Visual cue for "8 workers crunching these now."
  if (!props.done) {
    let marked = 0
    for (const s of built) {
      if (!s.imageUrl && marked < props.inFlightCount) {
        s.inFlight = true
        marked++
      }
    }
  }
  // Prepend the card back as the first slot so operators see it
  // alongside the 78 cards. Empty placeholder when not yet generated.
  built.unshift({
    name: 'Card Back',
    cardId: null,
    imageUrl: props.backUrl || '',
    inFlight: !props.backUrl && !props.done,
    isBack: true,
  })
  return built
})

const filledCount = computed(
  () => slots.value.filter(s => !!s.imageUrl && !s.isBack).length,
)
</script>

<style scoped>
.full-deck-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.grid-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.grid-title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 16px;
}
.grid-sub {
  color: rgba(212, 196, 232, 0.6);
  font-size: 12px;
  margin-top: 2px;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  background: rgba(201, 169, 78, 0.1);
  border: 1px solid rgba(201, 169, 78, 0.32);
  border-radius: 999px;
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #C9A94E;
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #C9A94E;
}
.dot.pulse {
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(201, 169, 78, 0.55); }
  50% { opacity: 0.55; box-shadow: 0 0 0 6px rgba(201, 169, 78, 0); }
}

.grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
}
@media (min-width: 900px) {
  .grid { grid-template-columns: repeat(8, minmax(0, 1fr)); }
}
@media (max-width: 600px) {
  .grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

.slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.slot.clickable { cursor: pointer; transition: transform 0.18s; }
.slot.clickable:hover { transform: translateY(-2px); }
.slot.clickable:hover .slot-frame {
  border-color: #C9A94E;
  box-shadow: 0 4px 14px rgba(201, 169, 78, 0.32);
}
.slot.inflight .slot-frame {
  border-color: rgba(201, 169, 78, 0.65);
  animation: inflight-pulse 1.4s ease-in-out infinite;
}
@keyframes inflight-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(201, 169, 78, 0.55); }
  50% { box-shadow: 0 0 0 4px rgba(201, 169, 78, 0); }
}
.spinner-dot {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(201, 169, 78, 0.3);
  border-top-color: #C9A94E;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.slot-frame {
  width: 100%;
  aspect-ratio: 2 / 3;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(195, 169, 255, 0.14);
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.4s, box-shadow 0.4s;
}
.slot.filled .slot-frame {
  border-color: rgba(201, 169, 78, 0.42);
  box-shadow: 0 0 12px rgba(201, 169, 78, 0.16);
}
/* The leading "Card Back" slot uses a cooler accent so it reads as
   a different artifact than the 78 cards beside it. */
.slot.slot-back .slot-frame {
  border-color: rgba(195, 169, 255, 0.5);
  box-shadow: 0 0 12px rgba(195, 169, 255, 0.18);
}
.slot.slot-back .slot-name {
  color: rgba(195, 169, 255, 0.75);
}
.slot-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.slot-placeholder {
  color: rgba(195, 169, 255, 0.25);
  font-size: 16px;
}
.slot-name {
  font-size: 9px;
  color: rgba(212, 196, 232, 0.5);
  text-align: center;
  line-height: 1.2;
  font-family: 'Cinzel', serif;
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
